import DeleteConfirmationDialog from '@components/DeleteConfirmation';
import CategoryUpdateModal from '@components/modal/CategoryUpdateModal';
import { closestCenter, DndContext, useDraggable, useDroppable } from '@dnd-kit/core';
import {
  useAddCodeFromCategory,
  useDeleteCategory,
  useRemoveCodeFromCategory,
} from '@hooks/deleteQueries';
import useConfirmationDialog from '@hooks/useDeleteConfirmation';
import { Icon } from '@iconify/react';
import { Close } from '@mui/icons-material';
import ClearIcon from '@mui/icons-material/Clear';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Popover,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import formatString from '@utils/formatString';
import { useCallback, useEffect, useRef, useState } from 'react';

function CodebookTableView({
  interviewCodebook,
  setSelectedCode,
  handleChipClick,
  setOpenCodeModal,
  openCodeModal,
  id,
  getColorByCategory,
  canEdit,
}) {
  const [expandSummary, setExpandSummary] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [openPopover, setOpenPopover] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [openCategoryModal, setOpenCategoryModal] = useState(false);
  const [addCategoryItem, setAddCategoryItem] = useState(null);
  const [sourceCategory, setSourceCategory] = useState(null);
  const [targetCategory, setTargetCategory] = useState(null);
  const [draggedCode, setDraggedCode] = useState(null);
  const [categories, setCategories] = useState(null);
  const [isCodeRemovable, setIsCodeRemovable] = useState(true);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;

    const sourceCatId = active.data.current.categoryId;
    const targetCatId = over.id;
    const draggedCode = active.id;
    setDraggedCode(draggedCode);

    if (sourceCatId === targetCatId) return;
    setAddCategoryItem(targetCatId);

    setCategories((prev) => {
      const sourceCat = prev.find((cat) => cat.id === sourceCatId);
      const targetCat = prev.find((cat) => cat.id === targetCatId);
      setSourceCategory(sourceCat);
      setTargetCategory(targetCat);

      return prev.map((cat) => {
        if (cat.id === sourceCatId) {
          return {
            ...cat,
            codes: cat.codes.filter((c) => c !== draggedCode),
          };
        } else if (cat.id === targetCatId) {
          return {
            ...cat,
            codes: [...cat.codes, draggedCode],
          };
        }
        return cat;
      });
    });
  };

  useEffect(() => {
    const transformed = Object.entries(interviewCodebook?.categories ?? {})
      .map(([key, value]) => ({
        id: key.replace(/\s+/g, '-').toLowerCase(),
        name: key,
        description: value.description,
        summary: value.summary,
        saliency: value.saliency,
        codes: value.codes_included,
      }))
      .sort((a, b) => b.saliency - a.saliency);

    setCategories(transformed);
  }, [interviewCodebook]);

  // dnd end

  const handleOpenPopover = (e) => {
    setOpenPopover(e.currentTarget);
  };
  const handleClosePopover = () => {
    setSelectedNode(null);
    setOpenPopover(null);
  };
  const { open, show, handleConfirm, handleClose } = useConfirmationDialog();
  const {
    open: openUnassignCode,
    show: showUnassignCode,
    handleConfirm: handleUnassignCodeConfirm,
    handleClose: handleUnassignCodeClose,
  } = useConfirmationDialog();

  const { mutateAsync, isPending: isDeletingCategory } = useDeleteCategory(handleClosePopover);
  const { mutateAsync: removeCodeFromCategoryMutateAsync, isPending: isRemovingCodeFromCategory } =
    useRemoveCodeFromCategory();
  const { mutateAsync: addCodeFromCategoryMutateAsync, isPending: isAddingCodeFromCategory } =
    useAddCodeFromCategory();

  const cleanupAfterSave = () => {
    handleCloseAddCategoryModal();
    setSourceCategory(null);
    setTargetCategory(null);
    setDraggedCode(null);
  };

  const handleSave = async () => {
    if (!draggedCode || !id) return;

    const newCategoryPayload = {
      study_id: id,
      category_name: targetCategory?.name,
      codes: [draggedCode],
    };

    try {
      await addCodeFromCategoryMutateAsync(newCategoryPayload);
      if (!isCodeRemovable) {
        cleanupAfterSave();
        return;
      }
      if (sourceCategory) {
        const removePayload = {
          study_id: id,
          category_name: sourceCategory?.name,
          codes: [draggedCode],
        };

        await removeCodeFromCategoryMutateAsync(removePayload);
      }

      cleanupAfterSave();
    } catch (error) {
      console.error('Failed to save or remove code. Please try again.', error?.detail || error);
    }
  };

  const handleDeleteItem = useCallback(
    (payload) => {
      show(async () => {
        mutateAsync(payload);
      });
    },
    [mutateAsync, show],
  );
  const handleRemoveCodeFromCategory = useCallback(
    (payload) => {
      showUnassignCode(async () => {
        removeCodeFromCategoryMutateAsync(payload);
      });
    },
    [removeCodeFromCategoryMutateAsync, showUnassignCode],
  );
  const handleCloseAddCategoryModal = () => {
    setAddCategoryItem(null);
    setSourceCategory(null);
    setTargetCategory(null);
    setDraggedCode(null);
    setIsCodeRemovable(true);
    const transformed = Object.entries(interviewCodebook?.categories ?? {})
      .map(([key, value]) => ({
        id: key.replace(/\s+/g, '-').toLowerCase(),
        name: key,
        description: value.description,
        summary: value.summary,
        saliency: value.saliency,
        codes: value.codes_included,
      }))
      .sort((a, b) => b.saliency - a.saliency);

    setCategories(transformed);
  };

  const toggleCategory = useCallback((categoryKey) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryKey)) {
        newSet.delete(categoryKey);
      } else {
        newSet.add(categoryKey);
      }
      return newSet;
    });
  }, []);
  const closeCategoryModal = () => {
    setOpenCategoryModal(false);
  };

  const open1 = Boolean(openPopover);

  return (
    <>
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <TableContainer sx={{ p: 0 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Category / Description</strong>
                </TableCell>
                <TableCell>
                  <strong>Summary</strong>
                </TableCell>
                <TableCell>
                  <strong>Codes</strong>
                </TableCell>
                <TableCell>
                  <strong>Action</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories?.map((category) => (
                <DroppableCategory
                  key={category.id}
                  category={category}
                  getColorByCategory={getColorByCategory}
                  expandSummary={expandSummary}
                  setExpandSummary={setExpandSummary}
                  handleRemoveCodeFromCategory={handleRemoveCodeFromCategory}
                  handleChipClick={handleChipClick}
                  setSelectedCode={setSelectedCode}
                  id={id}
                  openUnassignCode={openUnassignCode}
                  openCodeModal={openCodeModal}
                  selectedNode={selectedNode}
                  expandedCategories={expandedCategories}
                  toggleCategory={toggleCategory}
                  targetCategory={targetCategory}
                  sourceCategory={sourceCategory}
                  setSelectedNode={setSelectedNode}
                  setDeletingCategory={setDeletingCategory}
                  handleOpenPopover={handleOpenPopover}
                  canEdit={canEdit}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DndContext>
      {!openCodeModal && (
        <Dialog
          open={addCategoryItem ? true : false}
          onClose={handleCloseAddCategoryModal}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle
            sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, height: '56px' }}
          >
            Change code category
            <IconButton
              onClick={handleCloseAddCategoryModal}
              sx={{ ml: 'auto', cursor: 'pointer' }}
            >
              <Close sx={{ fontSize: 20 }} />
            </IconButton>
          </DialogTitle>
          <Divider />
          <DialogContent>
            <Box px={2} mt={1}>
              <RadioGroup
                value={isCodeRemovable ? 'move' : 'copy'}
                onChange={(e) => setIsCodeRemovable(e.target.value === 'move')}
              >
                <Box>
                  <Typography sx={{ marginLeft: '32px' }}>
                    <strong>{formatString(draggedCode ?? '')}</strong> code will be moved from {` `}
                    <strong>{formatString(sourceCategory?.name ?? '')}</strong> to{' '}
                    <strong>{formatString(targetCategory?.name ?? '')}</strong> category. After the
                    move, it will no longer appear in{' '}
                    <strong>{formatString(sourceCategory?.name ?? '')}</strong> category.
                  </Typography>
                </Box>
              </RadioGroup>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCloseAddCategoryModal} variant="default">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              color="error"
              disabled={isAddingCodeFromCategory || isRemovingCodeFromCategory}
            >
              {isAddingCodeFromCategory || isRemovingCodeFromCategory ? 'Saving' : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <Popover
        id="codebook"
        open={open1}
        anchorEl={openPopover}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 1 }} width={'150px'}>
          <Typography
            aria-describedby="download"
            onClick={() => setOpenCategoryModal(true)}
            sx={{
              px: 2,
              py: 1,
              cursor: 'pointer',
              borderRadius: 1,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
              },
            }}
          >
            Edit
          </Typography>
          <Typography
            sx={{
              px: 2,
              py: 1,
              cursor: 'pointer',
              color: 'red',
              borderRadius: 1,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
              },
            }}
            onClick={() => {
              handleDeleteItem(deletingCategory);
            }}
          >
            Delete
          </Typography>
        </Box>
      </Popover>
      <DeleteConfirmationDialog
        open={open}
        onClose={handleClose}
        onConfirm={handleConfirm}
        title="Delete Category"
        isPending={isDeletingCategory}
        content={`This action can’t be undone. Are you sure you want to delete this category?`}
        okText={isDeletingCategory ? 'Deleting' : 'Delete'}
      />

      <CategoryUpdateModal
        openCategoryModal={openCategoryModal}
        closeCategoryModal={closeCategoryModal}
        interviewCodebook={interviewCodebook}
        setOpenCodeModal={setOpenCodeModal}
        setSelectedCode={setSelectedCode}
        selectedNode={selectedNode}
        id={id}
        handleClosePopover={handleClosePopover}
        getColorByCategory={getColorByCategory}
      />
      <DeleteConfirmationDialog
        open={openUnassignCode}
        onClose={handleUnassignCodeClose}
        onConfirm={handleUnassignCodeConfirm}
        title="Unassign this code from Category"
        isPending={isRemovingCodeFromCategory}
        content={`Are you sure you want to unassign this code from this category?`}
        okText={isRemovingCodeFromCategory ? 'Unassigning' : 'Unassign'}
      />
    </>
  );
}

export default CodebookTableView;

function DroppableCategory({
  id,
  category,
  targetCategory,
  sourceCategory,
  getColorByCategory,
  expandSummary,
  setExpandSummary,
  handleRemoveCodeFromCategory,
  handleChipClick,
  setSelectedCode,
  openUnassignCode,
  openCodeModal,
  selectedNode,
  expandedCategories,
  toggleCategory,
  setSelectedNode,
  setDeletingCategory,
  handleOpenPopover,
  canEdit,
}) {
  const { setNodeRef } = useDroppable({ id: category.id });
  // console.log('canEdit from DroppableCategory:', canEdit);
  return (
    <TableRow
      ref={setNodeRef}
      sx={{
        backgroundColor:
          category.name === targetCategory?.name
            ? '#D1ECF1'
            : category.name === sourceCategory?.name
              ? '#FFF3CD'
              : '',
      }}
    >
      <TableCell>
        {' '}
        <Box>
          <Typography
            fontWeight={600}
          >{`${formatString(category.name)} (n=${category?.saliency})`}</Typography>
          <Divider sx={{ marginTop: '10px' }} />
          <span style={{ fontWeight: '600' }}>Definition</span> : {category?.description}
        </Box>
      </TableCell>

      <TableCell>
        {' '}
        <Box>
          <Box>
            {category?.summary && (
              <>
                <Typography mt={1}>
                  {expandSummary == category?.name
                    ? category?.summary
                    : `${category?.summary?.slice(0, 150)}...`}
                  <span
                    size="small"
                    style={{
                      fontWeight: '600',
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                      marginLeft: '10px',
                    }}
                  >
                    {expandSummary == category?.name ? (
                      <span
                        role="button"
                        tabIndex={0}
                        style={{ color: '#7d5ae8' }}
                        onClick={() => setExpandSummary(null)}
                        onKeyPress={() => setExpandSummary(null)}
                      >
                        See Less
                      </span>
                    ) : (
                      <span
                        role="button"
                        tabIndex={0}
                        style={{ color: '#7d5ae8' }}
                        onClick={() => setExpandSummary(category?.name)}
                        onKeyPress={() => setExpandSummary(category?.name)}
                      >
                        See More
                      </span>
                    )}
                  </span>
                </Typography>
              </>
            )}
            {category?.summary ? <></> : <Typography textAlign={'center'}>--</Typography>}
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {(expandedCategories?.has(category?.name)
            ? category?.codes
            : category?.codes.slice(0, 8)
          )?.map((code) => (
            <DraggableCode
              key={code}
              category={category}
              code={code}
              categoryId={category.id}
              getColorByCategory={getColorByCategory}
              handleRemoveCodeFromCategory={handleRemoveCodeFromCategory}
              handleChipClick={handleChipClick}
              setSelectedCode={setSelectedCode}
              openUnassignCode={openUnassignCode}
              openCodeModal={openCodeModal}
              canEdit={canEdit}
              id={id}
            />
          ))}
          {category?.codes?.length > 4 && (
            <span
              size="small"
              style={{
                fontWeight: '600',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                marginLeft: '10px',
              }}
            >
              {category?.codes.length > 8 && (
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 600,
                    cursor: 'pointer',
                    color: '#7d5ae8',
                    marginLeft: '10px',
                  }}
                  onClick={() => toggleCategory(category?.name)}
                >
                  {expandedCategories.has(category?.name) ? 'See Less' : 'See More'}
                </Typography>
              )}
            </span>
          )}
        </Box>
      </TableCell>
      <TableCell>
        {' '}
        <Box
          sx={{
            backgroundColor: selectedNode?.name === category?.name ? '#d2d2d2' : '',
            height: '40px',
            width: '40px',

            borderRadius: '50%',
            display: canEdit ? 'flex' : 'none',
            justifyContent: 'center',
            alignItems: 'center',
            ':hover': {
              backgroundColor: '#d2d2d2',
            },
          }}
        >
          <MoreHorizIcon
            aria-describedby="codebook"
            aria-label="More options"
            sx={{ cursor: 'pointer' }}
            variant="contained"
            onClick={(e) => {
              handleOpenPopover(e);
              setSelectedNode({
                name: category?.name,
                saliency: category?.saliency,
              });
              setDeletingCategory({
                study_id: id,
                category_name: category?.name,
                category_description: category?.description,
              });
            }}
          />
        </Box>
      </TableCell>
    </TableRow>
  );
}

function DraggableCode({
  code,
  categoryId,
  getColorByCategory,
  category,
  handleRemoveCodeFromCategory,
  handleChipClick,
  setSelectedCode,
  id,
  openUnassignCode,
  openCodeModal,
  canEdit,
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: code,
    data: { categoryId },
    disabled: !canEdit || openUnassignCode || openCodeModal,
  });

  const style =
    openUnassignCode || openCodeModal
      ? {}
      : {
          transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
          opacity: isDragging ? 0.5 : 1,
        };
  // console.log('canEdit from DraggableCode:', canEdit);
  return (
    <div style={style}>
      <CodeTag
        code={code}
        getColorByCategory={getColorByCategory}
        category={category}
        handleRemoveCodeFromCategory={handleRemoveCodeFromCategory}
        handleChipClick={handleChipClick}
        setSelectedCode={setSelectedCode}
        id={id}
        isDragging={isDragging}
        listeners={listeners}
        attributes={attributes}
        setNodeRef={setNodeRef}
        canEdit={canEdit}
      />
    </div>
  );
}
function CodeTag({
  code,
  isDragging,
  getColorByCategory,
  category,
  handleRemoveCodeFromCategory,
  handleChipClick,
  setSelectedCode,
  listeners,
  attributes,
  id,
  setNodeRef,
  canEdit,
}) {
  const [isHovered, setIsHovered] = useState(false);
  const startPos = useRef({ x: 0, y: 0 });

  const onPointerDown = (e) => {
    startPos.current = { x: e.clientX, y: e.clientY };
  };

  const onPointerUp = (e) => {
    const dx = e.clientX - startPos.current.x;
    const dy = e.clientY - startPos.current.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist < 5) {
      e.stopPropagation();
      e.preventDefault();
      handleChipClick(code);
      setSelectedCode({
        name: code,
        definition: category?.description,
        location: 0,
        saliency: category?.saliency,
        categoryKey: category?.name,
      });
    }
  };
  useEffect(() => {
    if (isDragging) {
      setIsHovered(false);
    }
  }, [isDragging]);

  const handleCodeRemove = () => {
    handleRemoveCodeFromCategory({
      study_id: id,
      category_name: category?.name,
      codes: [code],
    });
  };

  return (
    <Chip
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      label={
        <Box display="flex" alignItems="center" gap="5px">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <span
              ref={setNodeRef}
              {...attributes}
              {...listeners}
              aria-label="Drag to reorder"
              style={{
                cursor: 'move',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'max-width 0.3s ease',
                pointerEvents: isHovered ? 'auto' : 'none',
              }}
            >
              <Icon
                icon="mdi:drag"
                style={{
                  transform: isHovered ? 'rotate(90deg)' : 'rotate(90deg) scale(0.8)',
                  transition: 'transform 0.3s ease',
                  width: 22,
                  height: 22,
                }}
              />
            </span>
            <span
              onPointerDown={onPointerDown}
              onPointerUp={onPointerUp}
              style={{ cursor: 'pointer' }}
            >
              {formatString(code)}
            </span>
          </Box>
          {canEdit && (
            <ClearIcon
              style={{
                width: '16px',
                height: '16px',
                cursor: 'pointer',
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleCodeRemove();
              }}
            />
          )}
        </Box>
      }
      size="small"
      sx={{
        borderRadius: '4px',
        backgroundColor: !isDragging && getColorByCategory(category?.name ?? ''),
      }}
    />
  );
}
